// compareVersions.js
//
// DAS-2560: pure helper for the "New Version Available" prompt.
//
// Frontend version strings are dotted, monotonically increasing build stamps in
// the form YYYY.MM.DD.HHMM (e.g. "2026.06.08.2154"). isNewerVersion compares two
// of them numerically, segment by segment.
//
// Why this exists: the old VersionChecker compared two *server-side* values
// (deployed version.json vs the latest app_versions DB row) for plain inequality.
// When a deploy updated one but not the other they diverged permanently, so the
// popup fired for everyone and a refresh could never clear it (DAS-2560). The new
// checker anchors to the version the tab actually loaded with and only prompts
// when a strictly newer build is live — which this helper decides.

/**
 * Is `candidate` a strictly newer build stamp than `base`?
 *
 * @param {string|null|undefined} candidate  e.g. the live deployed version
 * @param {string|null|undefined} base       e.g. the version this tab loaded with
 * @returns {boolean} true only when candidate is newer than base.
 *
 * Behaviour:
 *  - Missing/empty input or equal strings -> false (never prompt).
 *  - Both parse as dotted integers -> numeric segment-wise comparison (shorter
 *    side zero-padded), so "2026.06.08.2154" > "2026.06.02.1709".
 *  - If either side is not parseable as dotted integers -> fall back to plain
 *    inequality (candidate !== base). This still cannot loop, because the caller
 *    anchors `base` to the version loaded at startup: after a refresh base equals
 *    the deployed version, so the comparison goes false.
 */
export function isNewerVersion(candidate, base) {
  if (!candidate || !base) return false;
  if (candidate === base) return false;

  const parse = (v) => String(v).split('.').map((seg) => Number(seg));
  const a = parse(candidate);
  const b = parse(base);

  const parseable = (arr) => arr.length > 0 && arr.every((n) => Number.isFinite(n));
  if (!parseable(a) || !parseable(b)) {
    // Unrecognised format — treat any difference as "changed" (safe, loop-free).
    return candidate !== base;
  }

  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av > bv) return true;
    if (av < bv) return false;
  }
  return false; // all segments equal
}

export default isNewerVersion;
